import type { ComponentPropsWithRef, MouseEvent, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import * as Slot from 'radix-ui/slot';
import { cx } from '../../utils/cx';
import { Icon } from '../../utils/icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks clicks while keeping focus, so screen readers hear the busy state. */
  loading?: boolean;
  iconStart?: LucideIcon;
  iconEnd?: LucideIcon;
  /** Set for arrows and chevrons so they mirror in RTL. */
  iconStartDirectional?: boolean;
  iconEndDirectional?: boolean;
  fullWidth?: boolean;
  /** Render the child element (e.g. a router link) with button styling. */
  asChild?: boolean;
  children?: ReactNode;
}

const ICON_SIZE: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 20 };

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  iconStart,
  iconEnd,
  iconStartDirectional,
  iconEndDirectional,
  fullWidth,
  asChild,
  className,
  disabled,
  onClick,
  children,
  type,
  ...rest
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  const blocked = Boolean(disabled) || loading;
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  return (
    <Comp
      {...(asChild ? {} : { type: type ?? 'button' })}
      className={cx('kd-button', fullWidth && 'kd-button--full', className)}
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      disabled={asChild ? undefined : disabled}
      aria-disabled={blocked || undefined}
      aria-busy={loading || undefined}
      onClick={handleClick}
      {...rest}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? (
            <span className="kd-spinner" aria-hidden />
          ) : (
            iconStart && (
              <Icon icon={iconStart} size={ICON_SIZE[size]} directional={iconStartDirectional} />
            )
          )}
          <span className="kd-button__label">{children}</span>
          {iconEnd && (
            <Icon icon={iconEnd} size={ICON_SIZE[size]} directional={iconEndDirectional} />
          )}
        </>
      )}
    </Comp>
  );
}
